// ============================================
// src/App/consentGate.js
// MA-04 — COPPA verifiable-parental-consent gate (pure core).
//
// This module is dependency-free (no supabase, no React) so it can be unit
// tested in a plain .mjs and imported by both .jsx (useCloudSync) and .tsx
// (handoff / settings). The gate that actually blocks the child-PII cloud
// write (`runGatedCloudCreate`) is the SAME function `createCloudStudentFn`
// calls — the bite test drives it directly, so a green test proves the real
// short-circuit, not a fixture.
// ============================================

// ── Configurable VPC (verifiable-parental-consent) method ────────────────
// The method is recorded by ENCODING it into consent_records.consent_type as
// `coppa_v1_<method>` (the prod table has no `method` column and we add none).
// Swapping the method never orphans prior consents because the gate matches on
// the `coppa_v1_` prefix. BETA LIMITATION: this changes only the stored token,
// not the verification mechanism (a stronger VPC — card / signed form — is
// future work; MA-05+). Overridable at build time via VITE_VPC_METHOD.
export const CONSENT_TYPE_PREFIX = "coppa_v1";
export const VPC_METHOD =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_VPC_METHOD) ||
  "beta_parent_attestation";

// MA-04 signed-consent method (COPPA §312.5(b)(2)(i) "signed form"). When the
// active method is `signed`, the gate is STRICTER than the prefix match below:
// it requires the EXACT `coppa_v1_signed` consent_type AND a completed signature
// (typed legal name + date + the 16 CFR Part 312 acknowledgement). This closes
// three holes a prefix match leaves open (RED-2 2026-07-16):
//   (a) an incomplete/pre-signature row (granted with no signature) opening the gate;
//   (b) the PREFIX TRAP — `coppa_v1_signed_incomplete` would match a prefix check;
//   (c) the LEGACY TRAP — a weaker `coppa_v1_beta_parent_attestation` / `_founder_backfill`
//       granted row satisfying the stronger signed regime.
export const SIGNED_METHOD = "signed";
export const SIGNED_CONSENT_TYPE = `${CONSENT_TYPE_PREFIX}_${SIGNED_METHOD}`; // "coppa_v1_signed"

/** True iff the active VPC method is the signed-form method. */
export function isSignedMethod(method = VPC_METHOD) {
  return String(method || "") === SIGNED_METHOD;
}

/** `coppa_v1_beta_parent_attestation` / `coppa_v1_signed` — the value written to consent_type. */
export function buildConsentType(method = VPC_METHOD) {
  return `${CONSENT_TYPE_PREFIX}_${String(method || "unknown")}`;
}

/**
 * True iff `sig` is a COMPLETE signed attestation: a non-empty typed legal name,
 * a non-empty typed date, and an explicit acknowledgement of the parent/guardian
 * + 16 CFR Part 312 statement. Anything missing = incomplete = the gate must NOT
 * open (kid stays local-only). This is the completion marker for a signed row.
 */
export function isCompleteSignature(sig) {
  return !!(
    sig &&
    typeof sig.name === "string" && sig.name.trim().length > 0 &&
    typeof sig.date === "string" && sig.date.trim().length > 0 &&
    sig.acknowledged === true
  );
}

/**
 * True iff `row` is a COMPLETED, un-revoked signed consent: EXACT
 * `coppa_v1_signed` type + `granted === true` + a complete signature. Used only
 * under the signed method — a legacy/weaker or incomplete row never qualifies.
 */
export function isCompletedSignedConsent(row) {
  return !!(
    row &&
    row.granted === true &&
    (row.revoked_at === null || row.revoked_at === undefined) &&
    row.consent_type === SIGNED_CONSENT_TYPE && // EXACT, not prefix
    isCompleteSignature(row.signature)
  );
}

// ── MA-464: school-authority DIRECT lane (teacher-role kid creation) ──────
// A teacher-role account creating a student attests SCHOOL AUTHORITY (COPPA's
// school-authorization path — the founder's classroom-first model, MA-DEC-0802-WS8),
// never parental guardianship. Account-scoped and EXACT-typed — deliberately NOT
// the per-class `school_authority_v1:<classId>` lane (schoolConsentGate.js), which
// stays roster-scoped, and NEVER satisfied by any `coppa_v1_*` row (lanes do not
// cross-satisfy, in either direction). Requires a complete typed attestation
// (name + date + acknowledgement) regardless of the parent lane's active VPC
// method: an operator asserting authority over other families' children is held
// to at least the signed-form bar, env-independently.
export const SCHOOL_DIRECT_CONSENT_TYPE = "school_authority_direct_v1";

/** True iff `row` is a COMPLETED, un-revoked school-authority-direct consent. */
export function isCompletedSchoolDirectConsent(row) {
  return !!(
    row &&
    row.granted === true &&
    (row.revoked_at === null || row.revoked_at === undefined) &&
    row.consent_type === SCHOOL_DIRECT_CONSENT_TYPE && // EXACT, not prefix
    isCompleteSignature(row.signature)
  );
}

/**
 * MA-464 — the role-aware gate predicate. Parent-role resolves EXACTLY as
 * `hasActiveConsent` always has (prefix/signed rules untouched). Teacher-role
 * requires a completed `school_authority_direct_v1` row; a `coppa_v1_*` row
 * never opens the teacher lane and a school-direct row never opens the parent
 * lane. Unknown roles resolve as parent (the `parseUserRole` default).
 */
export function hasActiveConsentForRole(rows, role = "parent", activeMethod = VPC_METHOD) {
  if (role === "teacher") {
    return Array.isArray(rows) ? rows.some(isCompletedSchoolDirectConsent) : false;
  }
  return hasActiveConsent(rows, activeMethod);
}

/**
 * True iff the parent has at least one GRANTED, un-revoked COPPA consent row
 * that satisfies the ACTIVE VPC method.
 * - `signed` method: requires a COMPLETED signed row (exact type + signature) —
 *   strict, and no weaker/legacy or incomplete row qualifies.
 * - other (legacy beta) methods: any `coppa_v1_*` granted, un-revoked row counts
 *   (backward-compatible with the pre-signed gate).
 * A `revoked_at` timestamp (MA-05) makes a row inactive; a non-COPPA consent
 * (e.g. marketing) never satisfies the gate.
 */
export function hasActiveConsent(rows, activeMethod = VPC_METHOD) {
  if (!Array.isArray(rows)) return false;
  if (isSignedMethod(activeMethod)) {
    return rows.some(isCompletedSignedConsent);
  }
  return rows.some(
    (r) =>
      r &&
      r.granted === true &&
      (r.revoked_at === null || r.revoked_at === undefined) &&
      typeof r.consent_type === "string" &&
      r.consent_type.startsWith(`${CONSENT_TYPE_PREFIX}_`),
  );
}

/**
 * R7 detector. Consent is PARENT-level (keyed by parent_id), so it is
 * all-or-nothing: if the parent has no active consent, every one of their kids
 * needs it. `kids` should be the LOCAL registry kids (which include local-only
 * kids that never reached the cloud) so a parent whose consent write failed
 * offline — leaving only local kids — is still flagged (red-team fix 1b), not
 * silently stuck at no-sync forever.
 */
export function studentsNeedingConsent(kids, consentRows, activeMethod = VPC_METHOD, role = "parent") {
  if (hasActiveConsentForRole(consentRows, role, activeMethod)) return [];
  return Array.isArray(kids) ? kids.filter(Boolean) : [];
}

/**
 * MA-315 — the Settings "COPPA mode" status label, derived from a REAL
 * consent read instead of a hardcoded "On". Five distinct states, never
 * collapsed: `enabled:false` (dev-bypass / no signed-in user — nothing to
 * check) and a query `isError` (the read failed) are both honestly UNKNOWN,
 * not "Off" — asserting "no consent" on a failed read would falsely alarm a
 * parent who actually has one. Only a SUCCEEDED read resolves to "On"/"Off".
 * Role-aware per MA-464: a teacher-role account's consent is the
 * `school_authority_direct_v1` lane, never `coppa_v1_*` — `role` threads into
 * `hasActiveConsentForRole` so a teacher's row reflects THEIR lane, not a
 * parent's.
 */
export function coppaModeLabel({
  enabled,
  isLoading,
  isError,
  isSuccess,
  rows,
  role = "parent",
  activeMethod = VPC_METHOD,
}) {
  if (!enabled) return "—";
  if (isError) return "Unable to verify";
  if (isSuccess) return hasActiveConsentForRole(rows, role, activeMethod) ? "On" : "Off";
  if (isLoading) return "Checking…";
  return "Checking…";
}

/**
 * The gate decision. Dev bypass short-circuits (e2e/dev has no real session).
 * A read error is treated as NO consent (fail-CLOSED) — the same local-only
 * outcome as being offline, so acceptance #2 (no PII to cloud without consent)
 * never reopens on a flaky read.
 */
export async function guardCloudChildWrite({ parentId, fetchConsent, devBypass = false, activeMethod = VPC_METHOD, role = "parent" }) {
  if (devBypass) return { allow: true, reason: "dev-bypass" };
  let rows = [];
  try {
    rows = await fetchConsent(parentId);
  } catch {
    rows = [];
  }
  return hasActiveConsentForRole(rows, role, activeMethod)
    ? { allow: true, reason: "consented" }
    : { allow: false, reason: "no-consent" };
}

/**
 * The real gated-create composition. `createCloudStudentFn` is a thin wrapper
 * over this, so the bite test exercises the ACTUAL short-circuit: `insert` is
 * never invoked when the gate denies. Returns whatever `insert` returns, or
 * null when blocked.
 */
export async function runGatedCloudCreate({ parentId, devBypass, fetchConsent, insert, onBlocked, activeMethod = VPC_METHOD, role = "parent" }) {
  const gate = await guardCloudChildWrite({ parentId, fetchConsent, devBypass, activeMethod, role });
  if (!gate.allow) {
    if (typeof onBlocked === "function") onBlocked(gate);
    return null;
  }
  return insert();
}
